import React, { useState, useEffect } from 'react';
import { 
    ArrowLeftIcon, 
    CheckIcon, 
    XIcon,
    EyeIcon,
    EyeOffIcon
} from '../components/icons';
import { KharonLogo } from '../components/Logo';
import { useStore } from '../store/useStore';

interface AuthScreenProps {
  onLogin: () => void;
  onRegister: () => void;
  onBack: () => void;
}

export const AuthScreen: React.FC<AuthScreenProps> = ({ onLogin, onRegister, onBack }) => {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot' | 'verify'>('login');
  const { 
    error: storeError, 
    isLoading: storeLoading, 
    login,
    register,
    loginWithGoogle, 
    forgotPassword, 
    verifyEmail: verifyEmailStore,
    setError,
    setIsLoading
  } = useStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const error = localError || storeError;
  const isLoading = storeLoading;

  useEffect(() => {
    // Limpiar errores al cambiar de modo
    setLocalError(null);
    setError(null);
  }, [mode, setError]);

  useEffect(() => {
    if (storeError) {
      console.log("[AuthScreen] Store Error Detected:", storeError);
    }
  }, [storeError]);

  const handleGoogleLogin = async () => {
    try {
        await loginWithGoogle();
        onLogin();
    } catch (err) {
        // Error ya manejado por zustand
    }
  };

  const validatePassword = (pass: string) => {
    if (pass.length < 8) return 'La contraseña debe tener al menos 8 caracteres.';
    if (!/[A-Z]/.test(pass)) return 'Debe incluir al menos una mayúscula.';
    if (!/[0-9]/.test(pass)) return 'Debe incluir al menos un número.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);
    setError(null);

    if (mode === 'login') {
      try {
        await login({ email, password, rememberMe });
        onLogin(); // Success callback
      } catch (err) {}
    } else if (mode === 'register') {
      const passError = validatePassword(password);
      if (passError) {
        setLocalError(passError);
        return;
      }
      if (!name || !email) {
        setLocalError('Por favor, completa todos los campos.');
        return;
      }
      try {
        await register({ name, email, password });
        setSuccessMessage('¡Cuenta creada! Te hemos enviado un enlace de confirmación a tu correo. Revisa tu bandeja de entrada (y spam) para poder entrar.');
        setMode('login'); // Switch back to login after registration success
      } catch (err) {}
    } else if (mode === 'forgot') {
      try {
        await forgotPassword(email);
        setSuccessMessage('Si tu correo está registrado, recibirás un enlace en unos minutos.');
      } catch (err: any) {
        setLocalError(translateError(err.code || err.message));
      }
    }
  };

  const isFormValid = () => {
    if (mode === 'login') return email.includes('@') && password.length >= 6;
    if (mode === 'register') return email.includes('@') && password.length >= 8 && name.trim().length > 2;
    if (mode === 'forgot') return email.includes('@');
    return false;
  };

  const translateError = (message: string) => {
    if (!message) return null;
    
    // Extract code if it's a full Firebase message
    const code = message.includes('auth/') 
      ? message.match(/auth\/[a-z0-9-]+/)?.[0] || message 
      : message;

    switch (code) {
      case 'auth/user-not-found': return 'No existe ninguna cuenta con este correo electrónico.';
      case 'auth/wrong-password': return 'La contraseña es incorrecta. Inténtalo de nuevo.';
      case 'auth/invalid-credential': return 'Contraseña o correo incorrectos.';
      case 'auth/email-already-in-use': return 'Este correo electrónico ya está registrado.';
      case 'auth/invalid-email': return 'El formato del correo electrónico no es válido.';
      case 'auth/weak-password': return 'La contraseña es muy débil (mínimo 6 caracteres).';
      case 'auth/operation-not-allowed': return 'El registro con email no está habilitado.';
      case 'auth/popup-closed-by-user': return 'Se cerró la ventana de Google antes de completar.';
      case 'auth/network-request-failed': return 'Error de red. Revisa tu conexión.';
      case 'auth/too-many-requests': return 'Demasiados intentos. Inténtalo más tarde.';
      case 'auth/unverified-email': return 'Debes verificar tu email antes de iniciar sesión.';
      default: 
        if (message.includes('Firebase')) return 'Error en la autenticación. Revisa tus datos.';
        return message;
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6 z-[200]">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-20 dark:opacity-10">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-300 rounded-full blur-[120px]"></div>
        <div className="absolute top-1/2 -right-24 w-80 h-80 bg-slate-300 rounded-full blur-[100px]"></div>
      </div>

      <div className="relative w-full max-w-[440px] animate-fade-in-up">
        <button 
            onClick={onBack}
            className="absolute -top-12 left-0 flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-900 dark:hover:text-white transition-colors"
        >
                    <ArrowLeftIcon className="w-5 h-5 text-slate-400 group-hover:text-primary-500 transition-colors" />
            Volver
        </button>

        <div className="bg-white dark:bg-slate-900 rounded-[32px] shadow-luxury p-10 border border-slate-100 dark:border-slate-800">
          <header className="text-center mb-10">
            <KharonLogo className="h-8 mx-auto text-slate-900 dark:text-white mb-8" />
            <h1 className="text-2xl font-serif font-bold text-slate-900 dark:text-white">
              {mode === 'login' && 'Bienvenido de nuevo'}
              {mode === 'register' && 'Crear nueva cuenta'}
              {mode === 'forgot' && 'Recuperar acceso'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-2 text-sm">
              {mode === 'forgot' ? 'Introduce tu email para enviarte un enlace de recuperación.' : 'Gestión Funeraria Inteligente'}
            </p>
          </header>

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'register' && (
              <div className="relative group">
                <input
                   type="text"
                   id="auth-name"
                   required
                   value={name}
                   onChange={(e) => setName(e.target.value)}
                  className="w-full px-5 pt-8 pb-3 bg-slate-50 dark:bg-slate-800 rounded-2xl border-2 border-transparent focus:border-primary-500 outline-none transition-all font-bold text-slate-900 dark:text-white peer"
                  placeholder=" "
                />
                <label htmlFor="auth-name" className={`absolute left-5 transition-all duration-200 pointer-events-none text-slate-400 font-bold ${name ? 'top-2 text-[10px] uppercase tracking-wider' : 'top-5 text-sm peer-focus:top-2 peer-focus:text-[10px] peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-primary-500'}`}>
                    Nombre Completo
                </label>
              </div>
            )}

            {(mode === 'login' || mode === 'register' || mode === 'forgot') && (
              <div className="relative group">
                <input
                  type="email"
                  id="auth-email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-5 pt-8 pb-3 bg-slate-50 dark:bg-slate-800 rounded-2xl border-2 border-transparent focus:border-primary-500 outline-none transition-all font-bold text-slate-900 dark:text-white peer"
                  placeholder=" "
                />
                <label htmlFor="auth-email" className={`absolute left-5 transition-all duration-200 pointer-events-none text-slate-400 font-bold ${email ? 'top-2 text-[10px] uppercase tracking-wider' : 'top-5 text-sm peer-focus:top-2 peer-focus:text-[10px] peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-primary-500'}`}>
                    Correo Electrónico
                </label>
              </div>
            )}

            {(mode === 'login' || mode === 'register') && (
              <div className="space-y-2">
                <div className="relative group">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="auth-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-5 pr-12 pt-8 pb-3 bg-slate-50 dark:bg-slate-800 rounded-2xl border-2 border-transparent focus:border-primary-500 outline-none transition-all font-bold text-slate-900 dark:text-white peer"
                    placeholder=" "
                  />
                  <label htmlFor="auth-password" className={`absolute left-5 transition-all duration-200 pointer-events-none text-slate-400 font-bold ${password ? 'top-2 text-[10px] uppercase tracking-wider' : 'top-5 text-sm peer-focus:top-2 peer-focus:text-[10px] peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-primary-500'}`}>
                      Contraseña
                  </label>
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-primary-500 transition-colors">
                      {showPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                
                {mode === 'login' && (
                    <div className="flex justify-between items-center px-1">
                      <label className="flex items-center gap-2 cursor-pointer group/chk">
                          <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="hidden" />
                          <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${rememberMe ? 'bg-primary-500 border-primary-500' : 'border-slate-300 dark:border-slate-600 group-hover/chk:border-primary-500'}`}>
                              {rememberMe && <CheckIcon className="w-3 h-3 text-white" />}
                          </div>
                          <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 select-none">Recuérdame</span>
                      </label>
                      <button type="button" onClick={() => setMode('forgot')} className="text-[10px] font-bold text-primary-600 dark:text-primary-400 hover:underline">
                        ¿Olvidaste tu contraseña?
                      </button>
                    </div>
                )}
              </div>
            )}

            {/* Mode: Verify removed as it used non-existent 6-digit code approach */}

            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-r-xl flex items-center gap-3 animate-fade-in shadow-sm">
                <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-xs font-bold text-red-800 dark:text-red-200">{translateError(error)}</p>
              </div>
            )}

            {successMessage && (
              <div className="p-4 bg-teal-50 dark:bg-teal-900/20 border-l-4 border-teal-500 rounded-r-xl flex items-center gap-3 animate-fade-in shadow-sm">
                <CheckIcon className="w-5 h-5 text-teal-500 flex-shrink-0" />
                <p className="text-xs font-bold text-teal-800 dark:text-teal-200">{successMessage}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !isFormValid()}
              className="w-full py-5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black rounded-2xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:active:scale-100 disabled:hover:scale-100 mt-4"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-3 border-current border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <span>
                  {mode === 'login' && 'Entrar al Sistema'}
                  {mode === 'register' && 'Registrar Cuenta'}
                  {mode === 'forgot' && 'Enviar Enlace'}
                </span>
              )}
            </button>

            {(mode === 'login' || mode === 'register') && (
              <>
                <div className="relative py-4">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100 dark:border-slate-800"></div></div>
                  <div className="relative flex justify-center text-[10px] font-black uppercase tracking-widest">
                    <span className="bg-white dark:bg-slate-900 px-4 text-slate-400">O continuar con</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  className="w-full py-4 bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-bold rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Continuar con Google</span>
                </button>
              </>
            )}
          </form>

          <footer className="mt-8 text-center">
            <button
              onClick={() => {
                if (mode === 'forgot') setMode('login');
                else setMode(mode === 'login' ? 'register' : 'login');
                setLocalError(null);
                setSuccessMessage(null);
              }}
              className="text-sm font-bold text-slate-500 dark:text-slate-400 hover:text-primary-600 transition-colors"
            >
              {mode === 'login' && '¿No tienes cuenta? Regístrate gratis'}
              {mode === 'register' && '¿Ya tienes cuenta? Inicia sesión'}
              {mode === 'forgot' && 'Volver al inicio de sesión'}
            </button>
          </footer>
        </div>
        
        <p className="text-center mt-8 text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-[0.3em]">
          Powered by Kharon Cloud
        </p>
      </div>
    </div>
  );
};
